/* eslint-disable restrict-imports/restrict-imports */
import React, { useState, useEffect, useRef } from 'react';
import { Button, IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import {
  AccountCircle,
  Add,
  AddPhotoAlternateOutlined,
  EditOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Menu,
  Mic,
  MoreVert,
  RefreshOutlined,
  ShareOutlined,
  ThumbDownAltOutlined,
  ThumbUpAltOutlined,
  Tune,
  VolumeUp,
} from '@material-ui/icons';
import { makeStyles } from 'design-system';
import cx from 'clsx';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { ChatMockProps, validateChats } from './utils';

const BulbaChatMobile: React.FC<ChatMockProps> = ({ attachment }: ChatMockProps) => {
  const isAttachmentValid = validateChats(attachment.attachment_body);
  const classes = useStyles();
  const [darkMode, setDarkMode] = useState<boolean>(!!attachment.darkMode);

  return (
    <div className={classes.container}>
      {isAttachmentValid ? (
        <>
          {attachment.attachment_body.map((chat: any, i: number) => (
            <div
              key={`chat-${i}`}
              className={cx(classes.singleChat, { [classes.singleChatDark]: darkMode })}
              style={{ width: attachment.width ?? 375, height: attachment.height ?? 680 }}
            >
              <header className={cx(classes.header, { [classes.headerDark]: darkMode })}>
                <IconButton size="large" aria-label="menu" sx={{ mr: 0.5 }}>
                  <Menu className={darkMode ? classes.iconDarkLight : classes.iconLightLight} />
                </IconButton>
                <p className={cx(classes.logoText, { [classes.logoTextDark]: darkMode })}>Bulba</p>
                <div className={classes.floatRight}>
                  <IconButton
                    size="large"
                    edge="start"
                    aria-label="new-chat"
                    sx={{
                      mr: 2,
                      height: 32,
                      width: 32,
                      backgroundColor: darkMode ? 'rgb(34, 35, 39)' : '#f5f4f9',
                    }}
                  >
                    <Add
                      className={cx(
                        classes.icon24,
                        darkMode ? classes.iconDarkLight : classes.iconLightLight,
                      )}
                    />
                  </IconButton>
                  <IconButton
                    size="large"
                    edge="start"
                    aria-label="options"
                    sx={{ mr: 2, height: 32, width: 32 }}
                  >
                    <MoreVert
                      className={cx(
                        classes.icon24,
                        darkMode ? classes.iconDarkLight : classes.iconLightLight,
                      )}
                    />
                  </IconButton>
                  <div style={{ cursor: 'pointer' }} onClick={() => setDarkMode(b => !b)}>
                    <AccountCircle className={cx(classes.icon32, classes.iconLightLight)} />
                  </div>
                </div>
              </header>
              <div className={classes.body}>
                {chat.map((turn: any, j: number) => (
                  <React.Fragment key={`chat-${i}-turn-${j}`}>
                    {turn.role === 'user' ? (
                      <ChatUserTurn
                        turn={turn}
                        darkMode={darkMode}
                        isLastTurn={j === chat.length - 1}
                      />
                    ) : (
                      <div className={cx(classes.bulbaTurn, { [classes.bulbaTurnDark]: darkMode })}>
                        <div className={classes.bulbaTurnHeader}>
                          {j === chat.length - 1 && (
                            <Button
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                color: darkMode ? 'rgb(196, 199, 197)' : '#1f1f1f',
                              }}
                              endIcon={
                                <RefreshOutlined
                                  style={{ color: darkMode ? 'rgb(168, 199, 250)' : '#1976d2' }}
                                />
                              }
                            >
                              Regenerate draft
                            </Button>
                          )}
                          <IconButton
                            size="large"
                            aria-label="play-ttv"
                            sx={{ mr: '-4px', mt: '-8px', mb: '8px' }}
                          >
                            <VolumeUp
                              className={darkMode ? classes.iconDarkDark : classes.iconLightDark}
                            />
                          </IconButton>
                        </div>
                        <div
                          className={cx(classes.bulbaTurnBody, {
                            [classes.bulbaTurnBodyDark]: darkMode,
                          })}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              marginTop: '-24px',
                              marginLeft: '60px',
                            }}
                          >
                            <SanitizedMarkdown
                              text={turn.text}
                              innerStyle={`line-height: 1.5rem; white-space: pre-wrap; word-break: break-word; ${
                                darkMode ? 'font-weight: 450;' : ''
                              }`}
                            />
                            <div className={classes.bulbaTurnFooter}>
                              <IconButton size="large" aria-label="thumb-up">
                                <ThumbUpAltOutlined
                                  className={
                                    darkMode ? classes.iconDarkDark : classes.iconLightDark
                                  }
                                />
                              </IconButton>
                              <IconButton size="large" aria-label="thumb-down">
                                <ThumbDownAltOutlined
                                  className={
                                    darkMode ? classes.iconDarkDark : classes.iconLightDark
                                  }
                                />
                              </IconButton>
                              {j === chat.length - 1 && (
                                <IconButton size="large" aria-label="tune">
                                  <Tune
                                    className={
                                      darkMode ? classes.iconDarkDark : classes.iconLightDark
                                    }
                                  />
                                </IconButton>
                              )}
                              <IconButton size="large" aria-label="share-response">
                                <ShareOutlined
                                  className={
                                    darkMode ? classes.iconDarkDark : classes.iconLightDark
                                  }
                                />
                              </IconButton>
                              <IconButton size="large" aria-label="response-options">
                                <MoreVert
                                  className={
                                    darkMode ? classes.iconDarkDark : classes.iconLightDark
                                  }
                                />
                              </IconButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <footer className={cx(classes.footer)}>
                <div className={classes.footerChat}>
                  <IconButton
                    size="large"
                    aria-label="add-photo"
                    style={{ flexShrink: 0, marginTop: 24 }}
                  >
                    <AddPhotoAlternateOutlined
                      className={darkMode ? classes.iconDarkDark : classes.iconLightDark}
                    />
                  </IconButton>
                  <div style={{ flexGrow: 1 }}>
                    <OutlinedInput
                      type="text"
                      fullWidth
                      disabled
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton aria-label="mic">
                            <Mic
                              className={darkMode ? classes.iconDarkDark : classes.iconLightDark}
                            />
                          </IconButton>
                        </InputAdornment>
                      }
                      placeholder="Enter a prompt here"
                      className={cx(classes.chatInput, { [classes.chatInputDark]: darkMode })}
                    />
                  </div>
                </div>
                <p
                  className={cx(classes.privacyNotice, {
                    [classes.privacyNoticeDark]: darkMode,
                  })}
                >
                  Bulba may display inaccurate or offensive information that doesn't represent
                  Gorilla's views.{' '}
                  <span style={{ fontWeight: 500, textDecoration: 'underline' }}>
                    Bulba Privacy Notice
                  </span>
                </p>
              </footer>
            </div>
          ))}
        </>
      ) : (
        <div>Invalid Bulba Chat Attachment</div>
      )}
    </div>
  );
};

const ChatUserTurn: React.FC<{ turn: any; darkMode: boolean; isLastTurn: boolean }> = ({
  turn,
  darkMode,
  isLastTurn,
}) => {
  const classes = useStyles();
  const chatTextRef = useRef<any>(null);
  const [isTextClamped, setIsTextClamped] = useState<boolean>(false);
  const [isTextExpanded, setIsTextExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (
      chatTextRef?.current &&
      chatTextRef.current?.scrollHeight > chatTextRef.current?.clientHeight
    )
      setIsTextClamped(true);
  }, [chatTextRef, chatTextRef?.current?.scrollHeight, chatTextRef?.current?.clientHeight]);

  return (
    <div
      className={cx(classes.userTurn, {
        [classes.userChatTextClampMargin]: !!isTextClamped && !isTextExpanded,
      })}
    >
      <div className={classes.userTurnIcon}>
        <AccountCircle
          className={cx(classes.icon32, classes.iconLightLight, classes.userTurnIconMargin)}
        />
      </div>
      <p
        className={cx(classes.userChatText, classes.userChatTextClamp, {
          [classes.userChatTextExpanded]: isTextExpanded,
          [classes.userChatTextDark]: darkMode,
          [classes.userChatTextClampMargin]: !!isTextClamped,
        })}
        ref={chatTextRef}
      >
        <SanitizedMarkdown
          text={turn.text}
          innerStyle={`line-height: 1.5rem; white-space: pre-wrap; word-break: break-word; ${
            darkMode ? 'font-weight: 450;' : ''
          }`}
        />
      </p>
      <div className={classes.iconColumn}>
        {!!isTextClamped && (
          <IconButton
            size="large"
            aria-label="expand-text"
            sx={{ mt: '-8px' }}
            onClick={() => setIsTextExpanded(b => !b)}
          >
            {isTextExpanded ? (
              <KeyboardArrowUp
                className={darkMode ? classes.iconDarkDark : classes.iconLightDark}
              />
            ) : (
              <KeyboardArrowDown
                className={darkMode ? classes.iconDarkDark : classes.iconLightDark}
              />
            )}
          </IconButton>
        )}
        {(!isTextClamped || isTextExpanded) && isLastTurn && (
          <IconButton size="large" aria-label="edit-prompt" sx={{ ml: 'auto', mt: '-8px' }}>
            <EditOutlined className={darkMode ? classes.iconDarkDark : classes.iconLightDark} />
          </IconButton>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    gap: 16,
    flexWrap: 'wrap',
    margin: '16px 0',
  },
  singleChat: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgb(243, 246, 252)',
    overflow: 'hidden',
  },
  singleChatDark: {
    backgroundColor: 'rgb(34, 35, 39)',
  },
  header: {
    height: 56,
    padding: '4px 4px 4px 8px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  headerDark: {
    backgroundColor: 'black',
  },
  logoText: {
    fontFamily: 'Helvetica Neue,sans-serif',
    fontSize: '1.3rem',
    fontWeight: 500,
    color: 'rgb(96, 99, 103)',
  },
  logoTextDark: {
    color: 'rgb(232, 234, 236)',
  },
  icon24: {
    transform: 'scale(3)',
  },
  icon32: {
    transform: 'scale(1.6)',
    marginRight: 12,
  },
  iconLightLight: {
    color: 'rgb(95, 99, 104)',
  },
  iconLightDark: {
    color: 'rgb(60, 64, 67)',
  },
  iconDarkLight: {
    color: '#e3e3e3',
  },
  iconDarkDark: {
    color: 'rgb(196, 199, 197)',
  },
  iconColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  floatRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  body: {
    overflowY: 'auto',
    padding: '0 16px',
  },
  userTurn: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'start',
    margin: '16px 0 16px 12px',
  },
  userTurnIcon: {
    height: 40,
    display: 'flex',
    alignItems: 'start',
    flexShrink: 0,
    marginRight: 16,
  },
  userTurnIconMargin: {
    marginTop: 4,
    marginLeft: 4,
    marginRight: 4,
  },
  userChatTextClampMargin: {
    margin: '16px 0px 48px 12px',
  },
  userChatText: {
    fontFamily: 'Helvetica Neue,sans-serif',
    fontSize: '1rem',
    lineHeight: '1.4rem',
    letterSpacing: 'normal',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    paddingTop: 10,
    margin: 0,
    marginTop: -24,
    marginBottom: -38,
  },
  userChatTextClamp: {
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': '4',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  userChatTextExpanded: {
    textOverflow: 'none',
    display: 'initial',
    '-webkit-line-clamp': 'none',
    '-webkit-box-orient': 'initial',
    overflow: 'initial',
  },
  userChatTextDark: {
    color: 'rgb(227, 227, 227)',
  },
  bulbaTurn: {
    backgroundColor: 'rgb(255, 255, 255)',
    borderRadius: 16,
    padding: '12px 8px 12px 0',
    display: 'flex',
    flexDirection: 'column',
  },
  bulbaTurnDark: {
    backgroundColor: 'rgb(19, 19, 20)',
  },
  bulbaTurnHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'end',
    gap: 12,
  },
  bulbaTurnBody: {
    fontFamily: 'Helvetica Neue,sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: '1.4rem',
    letterSpacing: 'normal',
    whiteSpace: 'pre-wrap',
    display: 'flex',
    flexDirection: 'row',
  },
  bulbaTurnBodyDark: {
    color: 'rgb(227, 227, 227)',
  },
  bulbaTurnFooter: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: -12,
  },
  sparkles: {
    flexShrink: 0,
    marginTop: '-26px',
  },
  footer: {
    marginTop: 'auto',
    position: 'relative',
    width: '100%',
    alignSelf: 'center',
  },
  footerChat: {
    display: 'flex',
    width: 'calc(100% - 16px)',
    alignItems: 'center',
  },
  footerScrollShadow: {
    // to implement when have time
    boxShadow: '0px -16px 16px -24px rgba(60, 64, 67, 0.80)',
  },
  chatInput: {
    marginTop: 16,
    borderRadius: '50px !important',
    fontFamily: 'Helvetica Neue,sans-serif !important',
    background: 'rgb(255, 255, 255) !important',
    borderColor: 'rgb(60, 64, 67) !important',
  },
  chatInputDark: {
    color: 'rgb(227, 227, 227) !important',
    '-webkit-text-fill-color': 'rgb(227, 227, 227) !important',
    '&.MuiInputBase .MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
      color: 'rgb(227, 227, 227) !important',
      '-webkit-text-fill-color': 'rgb(227, 227, 227) !important',
    },
    background: 'rgb(19, 19, 20) !important',
    borderColor: 'rgb(227, 227, 227) !important',
  },
  privacyNotice: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontFamily: 'Helvetica Neue,sans-serif',
    letterSpacing: '.1px',
    margin: '12px 24px',
    textAlign: 'center',
  },
  privacyNoticeDark: {
    color: 'rgb(227, 227, 227)',
  },
});

export default BulbaChatMobile;
